import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  compareContract,
  environmentReportSchema,
  toolboxVersionResponseSchema,
  type ContractSupport,
  type EnvironmentReport,
  type FogbugzToAdoToolsetBridge,
  type FogbugzToolsetBridge,
  type ToolboxBridge,
  type ToolboxTitleBar,
  type ToolboxVersionResponse,
  type ToolsetId,
} from "@ttc-spectree/toolbox-contract";
import { api } from "@/lib/api/client";

/**
 * Access to the SpecTree Toolbox desktop app from SpecTree's pages (epic
 * PI-SPEC-SW-E279, task PI-SPEC-SW-1395-4).
 *
 * The app opens SpecTree in its window and its preload script sets
 * `window.spectreeToolbox` before any page script runs. In a plain browser it
 * is absent. The app and SpecTree release separately, so before a page shows
 * an app-only action it checks that the app's contract number for the
 * toolset is inside the range SpecTree serves (`GET /api/v1/version`).
 */

/**
 * Where people learn to install SpecTree Toolbox. The setup guide (PI-SPEC-SW-1394)
 * lives with the app; until it is published this points at the app's README.
 */
export const TOOLBOX_SETUP_GUIDE_URL = "https://github.com/TTC-SpecTree/SpecTree-Toolbox#readme";

/** The bridge when the page runs inside SpecTree Toolbox, else null. */
export function getToolboxBridge(): ToolboxBridge | null {
  if (typeof window === "undefined") return null;
  return window.spectreeToolbox ?? null;
}

/**
 * The bridge for a component. Read once: the preload script sets it before
 * the page loads and it never changes while the page is open.
 */
export function useToolboxBridge(): ToolboxBridge | null {
  const [bridge] = useState(getToolboxBridge);
  return bridge;
}

/**
 * The fogbugz toolset's half of the bridge (contract fogbugz 1, epic
 * PI-SPEC-SW-E307): `summarizeEvtx` and `cancel`, and "Look into the code"
 * (`listCodeRefs`, `investigateCode`, `askCode`, `closeCodeThread`, `onCodeProgress`; feature
 * PI-SPEC-SW-1604, app only, never posted). Null in a browser and in an
 * app without the fogbugz toolset; check `useToolsetSupport("fogbugz")`
 * before offering it, so an app whose contract SpecTree does not serve shows
 * the update words instead.
 */
export function useFogbugzBridge(): FogbugzToolsetBridge | null {
  return useToolboxBridge()?.fogbugz ?? null;
}

/**
 * The fogbugz-to-ado toolset's half of the bridge (contract fogbugz-to-ado 1,
 * epic PI-SPEC-SW-E311): `checkRepos`, `readSlots`, `draftBug`, `cancel` and
 * `onProgress`. Null in a browser and in an app without the toolset; check
 * `useToolsetSupport("fogbugz-to-ado")` before offering it.
 */
export function useFogbugzToAdoBridge(): FogbugzToAdoToolsetBridge | null {
  return useToolboxBridge()?.fogbugzToAdo ?? null;
}

export const toolboxKeys = {
  all: ["toolbox"] as const,
  version: () => [...toolboxKeys.all, "version"] as const,
  environment: () => [...toolboxKeys.all, "environment"] as const,
};

/** `GET /api/v1/version`, parsed with the contract's schema (only the fields the Toolbox reads). */
export function useSpecTreeVersion() {
  return useQuery({
    queryKey: toolboxKeys.version(),
    queryFn: async (): Promise<ToolboxVersionResponse> =>
      toolboxVersionResponseSchema.parse(await api.get<unknown>("/version")),
    staleTime: 5 * 60_000,
  });
}

/**
 * Whether a toolset can be used here:
 * - `browser`: not inside SpecTree Toolbox;
 * - `supported`: the app's contract number is inside SpecTree's range;
 * - `app-update-needed`: the app is older than SpecTree serves, or does not ship the toolset;
 * - `spectree-update-needed`: the app is newer than this SpecTree serves (or SpecTree
 *   reports no range for the toolset).
 */
export type ToolsetSupport = "browser" | ContractSupport;

/** Decide support from the bridge and the version response. Pure; exported for tests. */
export function toolsetSupportOf(
  bridge: ToolboxBridge | null,
  version: ToolboxVersionResponse | undefined,
  toolset: ToolsetId,
): ToolsetSupport | undefined {
  if (!bridge) return "browser";
  const client = bridge.app.contracts[toolset];
  if (client === undefined) return "app-update-needed";
  if (!version) return undefined;
  const server = version.toolboxContracts?.[toolset];
  if (!server) return "spectree-update-needed";
  return compareContract(server, client);
}

/**
 * The support state of one toolset. `undefined` while SpecTree's contract
 * range is loading (never in a browser, which needs no request).
 */
export function useToolsetSupport(toolset: ToolsetId): ToolsetSupport | undefined {
  const bridge = useToolboxBridge();
  const version = useSpecTreeVersion();
  return toolsetSupportOf(bridge, version.data, toolset);
}

/**
 * The first-launch checks (`bridge.app.checkEnvironment()`), shared with the
 * setup page's query key. Disabled in a browser. Re-checked at most every
 * five minutes by the title bar's Claude chip.
 */
export function useToolboxEnvironment(options: { staleTime?: number } = {}) {
  const bridge = useToolboxBridge();
  return useQuery({
    queryKey: toolboxKeys.environment(),
    queryFn: async (): Promise<EnvironmentReport> => {
      if (!bridge) throw new Error("SpecTree Toolbox is not available");
      return environmentReportSchema.parse(await bridge.app.checkEnvironment());
    },
    enabled: bridge !== null,
    retry: false,
    staleTime: options.staleTime ?? 5 * 60_000,
  });
}

/**
 * Contract 2: the window frame the page draws its title bar inside, or
 * undefined (a browser, or a contract-1 app that keeps its native frame and
 * needs nothing reserved).
 */
export function toolboxTitleBar(bridge: ToolboxBridge | null): ToolboxTitleBar | undefined {
  return bridge?.app.titleBar;
}
